import React from 'react';
import PropTypes from 'prop-types';
import SelectLoginOtpChannel from './SelectLoginOtpChannel';
import LoginOtpVerification from './LoginOtpVerification';
import * as userRepo from '../../../shared/repos/graphql/user';

/**
 * OTP Challenge Flow Orchestrator
 * Manages state machine for email/password login with OTP challenge
 * Handles: channel selection → OTP verification → success
 */
class LoginOtpChallenge extends React.Component {
  constructor(props) {
    super(props);
    const {
      availableChannels,
      challengeId,
      mustSelectChannel,
      attemptsRemaining,
      maxAttempts,
      resendAvailableInSeconds,
      customerEmail,
      customerPhone,
      customerPhoneCode,
    } = props;

    const normalizedChannels = Array.isArray(availableChannels)
      ? availableChannels.filter(Boolean)
      : [];
    const shouldSelectChannel = mustSelectChannel ?? true;

    this.state = {
      // Challenge data from login response
      challengeId: challengeId || null,
      availableChannels: normalizedChannels,
      attemptsRemaining: attemptsRemaining ?? 5,
      maxAttempts: maxAttempts ?? 5,
      resendAvailableInSeconds: resendAvailableInSeconds ?? 0,

      // Customer data for masking
      customerEmail: customerEmail || '',
      customerPhone: customerPhone || '',
      customerPhoneCode: customerPhoneCode || '+1',

      // Flow state
      selectedChannel: shouldSelectChannel ? null : (normalizedChannels[0] || null),
      showOtpInput: !shouldSelectChannel,
      loading: false,
      error: null,
    };
  }

  /**
   * Handle channel selection
   */
  handleChannelSelected = async (channel) => {
    const { challengeId } = this.state;

    this.setState({ loading: true, error: null });

    try {
      const result = await userRepo.selectLoginOtpChannel(challengeId, channel);

      if (result.data?.selectLoginOtpChannel) {
        const challengeData = result.data.selectLoginOtpChannel;

        this.setState({
          selectedChannel: channel,
          showOtpInput: true,
          loading: false,
          resendAvailableInSeconds: challengeData.resendAvailableInSeconds || 0,
          attemptsRemaining: challengeData.attemptsRemaining,
        });
      }
    } catch (err) {
      const errorMsg = this.getErrorMessage(err);

      this.setState({
        error: errorMsg,
        loading: false,
      });
    }
  };

  /**
   * Handle OTP resend
   */
  handleResendOtp = async () => {
    const { challengeId } = this.state;

    this.setState({ loading: true, error: null });

    try {
      const result = await userRepo.resendLoginOtp(challengeId);

      if (result.data?.resendLoginOtp) {
        const challengeData = result.data.resendLoginOtp;

        this.setState({
          loading: false,
          resendAvailableInSeconds: challengeData.resendAvailableInSeconds || 0,
          attemptsRemaining: challengeData.attemptsRemaining,
          error: null,
        });

        return challengeData;
      }

      this.setState({ loading: false });
      return null;
    } catch (err) {
      const errorMsg = this.getErrorMessage(err);

      this.setState({
        error: errorMsg,
        loading: false,
      });

      throw err;
    }
  };

  /**
   * Handle OTP verification
   */
  handleOtpCompletion = async (otp, trustDevice) => {
    const { challengeId, attemptsRemaining } = this.state;
    const { onLoginSuccess } = this.props;

    this.setState({ loading: true, error: null });

    try {
      const result = await userRepo.completeLoginOtp(challengeId, otp, trustDevice);

      if (result.data?.completeLoginOtp) {
        const authData = result.data.completeLoginOtp;
        const { token } = authData;
        const { customer } = authData;

        // Call parent success callback
        if (onLoginSuccess) {
          onLoginSuccess(token, customer, {
            phoneVerificationRequired: authData.phoneVerificationRequired,
            loginOtpChallenge: authData.loginOtpChallenge,
          });
        }
      }
    } catch (err) {
      const { onOtpVerifyFailed } = this.props;
      const errorMsg = this.getErrorMessage(err);
      const newAttemptsRemaining = attemptsRemaining - 1;

      if (onOtpVerifyFailed) {
        onOtpVerifyFailed();
      }

      this.setState({
        error: errorMsg,
        loading: false,
        attemptsRemaining: Math.max(0, newAttemptsRemaining),
      });
    }
  };

  /**
   * Parse error messages for user display
   */
  getErrorMessage = (error) => {
    if (!error?.message) {
      return 'Something went wrong! Please try again.';
    }

    return error.message;
  };

  render() {
    const {
      challengeId,
      showOtpInput,
      selectedChannel,
      availableChannels,
      customerEmail,
      customerPhone,
      customerPhoneCode,
      loading,
      error,
      attemptsRemaining,
      maxAttempts,
      resendAvailableInSeconds,
    } = this.state;
    const { hideTrustDeviceOption } = this.props;

    // CHANNEL SELECTION SCREEN
    if (!showOtpInput) {
      return (
        <SelectLoginOtpChannel
          availableChannels={availableChannels}
          customerEmail={customerEmail}
          customerPhone={customerPhone}
          customerPhoneCode={customerPhoneCode}
          onChannelSelected={this.handleChannelSelected}
          loading={loading}
          error={error}
        />
      );
    }

    // OTP VERIFICATION SCREEN
    return (
      <LoginOtpVerification
        challengeId={challengeId}
        channel={selectedChannel}
        onOtpVerified={this.handleOtpCompletion}
        onResendOtp={this.handleResendOtp}
        loading={loading}
        error={error}
        resendAvailableInSeconds={resendAvailableInSeconds}
        attemptsRemaining={attemptsRemaining}
        maxAttempts={maxAttempts}
        hideTrustDeviceOption={hideTrustDeviceOption}
      />
    );
  }
}

LoginOtpChallenge.propTypes = {
  challengeId: PropTypes.string,
  availableChannels: PropTypes.arrayOf(PropTypes.oneOf(['EMAIL', 'SMS'])),
  mustSelectChannel: PropTypes.bool,
  attemptsRemaining: PropTypes.number,
  maxAttempts: PropTypes.number,
  resendAvailableInSeconds: PropTypes.number,
  customerEmail: PropTypes.string,
  customerPhone: PropTypes.string,
  customerPhoneCode: PropTypes.string,
  onLoginSuccess: PropTypes.func,
  onOtpVerifyFailed: PropTypes.func,
  hideTrustDeviceOption: PropTypes.bool,
};

LoginOtpChallenge.defaultProps = {
  challengeId: null,
  availableChannels: [],
  mustSelectChannel: true,
  attemptsRemaining: 5,
  maxAttempts: 5,
  resendAvailableInSeconds: 0,
  customerEmail: '',
  customerPhone: '',
  customerPhoneCode: '+1',
  onLoginSuccess: null,
  onOtpVerifyFailed: null,
  hideTrustDeviceOption: false,
};

export default LoginOtpChallenge;
